import { formatAud } from '@/pages/blueprint-lite/utils/formatters';

import type { CalculationSettings } from '../config/calculationSettings';
import type {
  AustralianState,
  CapitalGainsProjection,
  PropertyUse,
  TaxResidency,
} from '../types';

const STATE_OUTLOOK: Record<AustralianState, string> = {
  NSW: 'Sydney and NSW remain the highest-demand market for expat buyers — strong long-term capital growth driven by population density and infrastructure.',
  VIC: 'Melbourne offers strong yields and capital growth fundamentals, particularly in inner-city and growth corridor markets.',
  QLD: 'Queensland continues to attract interstate migration and expat buyers, with Brisbane infrastructure driving medium-term appreciation.',
  WA: 'Perth has demonstrated strong recent growth; long-term fundamentals tied to resources and population inflow.',
  SA: 'Adelaide offers relative affordability with improving growth; lower volatility than east coast capitals.',
  TAS: 'Hobart and the wider Tasmanian market have benefited from interstate migration and lifestyle demand; thinner liquidity warrants a longer-horizon strategy.',
  NT: 'The Northern Territory market is small and resource-cycle-sensitive; growth fundamentals depend heavily on regional infrastructure and migration policy.',
  ACT: 'Canberra delivers stable, government-sector-driven demand; lower cyclical volatility than capital cities.',
};

export interface CapitalGainsInput {
  hasPurchase: boolean;
  purchasePrice: number;
  state: AustralianState;
  propertyUse: PropertyUse;
  taxResidency: TaxResidency;
  settings: CalculationSettings;
}

function cgtPlanningFor(r: TaxResidency): string {
  if (r === 'non_tax_resident' || r === 'part_year_tax_resident_leaving_aus') {
    return 'As a non-resident at time of sale, the 50% CGT discount does not apply and a 12.5% Foreign Resident Capital Gains Withholding is deducted from gross proceeds. Plan disposal timing carefully — re-establishing tax residency before sale can restore discount access on accruals from that point forward.';
  }
  if (r === 'part_year_tax_non_resident_returning_aus') {
    return 'Returning to Australian tax residency means apportionment applies: capital growth accrued during your resident period may qualify for the 50% CGT discount (12-month hold required); growth accrued while non-resident does not. Document residency start date precisely.';
  }
  if (r === 'unsure') {
    return 'CGT treatment depends entirely on tax residency at the time of sale — discount eligibility, withholding rate, and marginal-rate schedule all differ. Resolve residency status with an ODIN tax agent before relying on these projections.';
  }
  return 'As an Australian tax resident, the 50% CGT discount applies to assets held more than 12 months. Confirm your residency status remains current at the planned time of any sale to retain discount eligibility.';
}

export function computeCapitalGains(
  input: CapitalGainsInput,
): CapitalGainsProjection | null {
  if (!input.hasPurchase || input.purchasePrice <= 0) return null;

  const yearFive = Math.round(
    input.purchasePrice * Math.pow(1 + input.settings.capitalGains5yrRate, 5),
  );
  const yearSeven = Math.round(
    input.purchasePrice * Math.pow(1 + input.settings.capitalGains7yrRate, 7),
  );
  const equityGainYearFive = yearFive - input.purchasePrice;

  const stateOutlook =
    STATE_OUTLOOK[input.state] ??
    'Strong long-term fundamentals for the target market.';

  const equityNote = `At 5% annual growth, your ${formatAud(
    input.purchasePrice,
  )} property would reach approximately ${formatAud(yearFive)} in Year 5 — an illustrative equity gain of ${formatAud(equityGainYearFive)} before costs and tax.`;

  const cgtPlanning = cgtPlanningFor(input.taxResidency);

  const considerations = [
    'Hold minimum 12 months to access the 50% CGT discount — short-term sale eliminates this benefit.',
    'Confirm your Australian tax residency status at time of any planned sale — this determines which CGT regime applies.',
    `${input.state} property market fundamentals support a ${
      input.propertyUse === 'investment' ? 'medium-to-long hold' : 'long-term hold'
    } strategy for optimal capital return.`,
  ];

  return {
    yearFive,
    yearSeven,
    equityGainYearFive,
    stateOutlook,
    equityNote,
    cgtPlanning,
    considerations,
  };
}
